import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { normalizeLocale } from '../../../i18n';
import { VOICE_OPTIONS } from '../../ai-assistant-settings/constants/voiceOptions';
import VoiceSampleCard from '../../ai-assistant-settings/components/VoiceSampleCard';
import { DEMO_CALL_LIMIT } from '../../../lib/api/demoCall';

const DEMO_AGENT_IDS = {
  'anna-dutch': 'a55961f4-44ea-40af-b24d-26473c6848f9',
  'sofia-english': '87756766-83b3-459d-9625-b883a996b882',
};

const DEMO_VOICE_OPTIONS = VOICE_OPTIONS
  .filter((voice) => voice.id === 'anna-dutch' || voice.id === 'sofia-english')
  .map((voice) => ({
    ...voice,
    vapiAgentId: DEMO_AGENT_IDS[voice.id] || voice.vapiAgentId,
    sampleUrl: undefined,
  }));

export const getDefaultDemoVoiceId = (locale) => (
  normalizeLocale(locale) === 'nl' ? 'anna-dutch' : 'sofia-english'
);

const DemoTestCallModal = ({
  isOpen,
  onClose,
  preferredLocale,
  onStartCall,
  starting = false,
  waiting = false,
  error = '',
  usedCalls = 0,
  callLimit = DEMO_CALL_LIMIT,
}) => {
  const { t } = useTranslation();
  const [selectedVoiceId, setSelectedVoiceId] = useState(() => getDefaultDemoVoiceId(preferredLocale));

  useEffect(() => {
    if (!isOpen) return;
    setSelectedVoiceId(getDefaultDemoVoiceId(preferredLocale));
  }, [isOpen, preferredLocale]);

  if (!isOpen) return null;

  const selectedVoice = DEMO_VOICE_OPTIONS.find((voice) => voice.id === selectedVoiceId)
    || DEMO_VOICE_OPTIONS[0];

  const handleBackdropClick = (event) => {
    if (starting) return;
    if (event.target === event.currentTarget) onClose();
  };

  const handleStartCall = () => {
    onStartCall?.(selectedVoice);
  };

  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-test-call-title"
    >
      <div
        className="bg-card rounded-lg border border-border shadow-elevation-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto p-4 md:p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4 md:mb-6">
          <div>
            <h2
              id="demo-test-call-title"
              className="text-lg md:text-xl font-semibold text-foreground mb-2"
            >
              {t('ai.voiceSelection')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('ai.voiceSelectionHint')}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {t('demo.callLimitHint', { limit: callLimit })}
              {' '}
              {t('demo.callLimitUsed', { used: usedCalls, limit: callLimit })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={starting}
            className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 disabled:opacity-50"
            aria-label={t('common.close')}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
          {DEMO_VOICE_OPTIONS.map((voice) => (
            <VoiceSampleCard
              key={voice.id}
              voice={voice}
              isSelected={selectedVoiceId === voice.id}
              onSelect={() => {
                if (starting) return;
                setSelectedVoiceId(voice.id);
              }}
            />
          ))}
        </div>

        {error && !waiting && (
          <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive text-center">
            {error}
          </div>
        )}

        {waiting ? (
          <div className="flex flex-col items-center text-center px-4 py-2" role="status" aria-live="polite">
            <Icon name="Phone" size={28} className="text-primary mb-3" />
            <p className="text-sm md:text-base text-foreground leading-relaxed">
              {t('demo.callWaiting')}
            </p>
          </div>
        ) : (
          <div className="flex justify-center">
            <Button
              type="button"
              onClick={handleStartCall}
              loading={starting}
              disabled={starting || usedCalls >= callLimit}
              iconName={starting ? null : 'Phone'}
              iconPosition="right"
            >
              {t('demo.startCall')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoTestCallModal;
